import { useAppSelector } from '@/app/redux/hooks';
import { useCurrentOrganisationId } from '@/app/redux/hooks/organisation';
import { selectSessionUserDetails } from '@/app/redux/slices/sessionSlice';
import { notifyZapier } from '@/common/utils/zapier';
import { WarningCircleIcon } from '@phosphor-icons/react';
import { Alert, AlertDescription } from '@shadcn/components/alert';
import { Button } from '@shadcn/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@shadcn/components/dialog';
import { Input } from '@shadcn/components/input';
import { Label } from '@shadcn/components/label';
import { Textarea } from '@shadcn/components/textarea';
import { FormEvent, useState } from 'react';
import { toast } from 'sonner';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  requireEmail?: boolean;
  title?: string;
  description?: string;
}

export default function FeedbackModal({
  isOpen,
  onClose,
  requireEmail = false,
  title = 'Give Feedback',
  description = "We'd love to hear your thoughts! Share feedback, report issues, or suggest improvements.",
}: FeedbackModalProps) {
  const { currentOrganisationId } = useCurrentOrganisationId();
  const user = useAppSelector(selectSessionUserDetails);
  const userId = user?.uid;
  const userEmail = user?.email;

  const [inputEmail, setInputEmail] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [isSending, setIsSending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (requireEmail && !isValidEmail(inputEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsSending(true);
    setError(null);

    try {
      await notifyZapier({
        hook: 'feedback',
        userId: userId ?? 'Unknown',
        organisationId: currentOrganisationId ?? 'Unknown',
        email: requireEmail ? inputEmail : (userEmail ?? 'Unknown'),
        message,
      });

      toast.success('Message sent! Thank you for your feedback.');
      handleClose();
    } catch (err) {
      console.error('Error sending feedback:', err);
      setError(
        'Something went wrong. Please try again or contact rupert@spellcaster.education directly.',
      );
    }
    setIsSending(false);
  };

  const handleClose = () => {
    setError(null);
    setInputEmail('');
    setMessage('');
    onClose();
  };

  const isSubmitDisabled =
    isSending || !message.trim() || (requireEmail && !inputEmail.trim());

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="text-xl">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <WarningCircleIcon size={16} className="ml-2" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {requireEmail && (
            <div className="space-y-2">
              <Label htmlFor="feedback-email">Email address</Label>
              <Input
                id="feedback-email"
                type="email"
                value={inputEmail}
                onChange={(e) => setInputEmail(e.target.value)}
                placeholder="your@email.com"
                required={requireEmail}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="feedback-message">Your feedback</Label>
            <Textarea
              id="feedback-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Tell us what you think..."
              className="min-h-[150px] resize-none"
              required
            />
          </div>

          <div className="flex items-center justify-between pt-4">
            <p className="text-sm text-muted-foreground">
              Or email us at{' '}
              <a
                href="mailto:rupert@spellcaster.education"
                className="text-primary hover:underline"
              >
                rupert@spellcaster.education
              </a>
            </p>
            <Button
              type="submit"
              disabled={isSubmitDisabled}
              loading={isSending}
            >
              Send feedback
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
